import {
	type BestMatchCriteria,
	type PexelsAsset,
	inferPexelsAspectRatioPreference,
	searchPexelsImages,
	searchPexelsVideos,
} from "./pexels-utils";
import {
	hasGoogleCseConfig,
	searchGoogleCseImages,
} from "./google-cse-images";
import {
	hasTavilyApiKey,
	tavilySearch,
} from "./tavily";
import {
	type WikimediaAsset,
	searchWikimediaAssets,
} from "./wikimedia-utils";

export type StockProviderId = "pexels" | "wikimedia" | "tavily" | "google";

export interface StockAsset {
	id: string;
	provider: StockProviderId;
	fileUrl: string;
	fileType: "image" | "video";
	width: number;
	height: number;
	duration?: number;
	attribution: string;
	sourcePageUrl: string;
	/** Provider-native id (pexels numeric / commons page id). */
	externalId: string;
	/** Optional poster / preview frame for videos. */
	previewUrl?: string;
}

export interface SearchStockAssetsInput {
	query: string;
	type: "image" | "video";
	aspectRatio?: "vertical" | "horizontal" | "square";
	/** Preferred minimum duration for videos (seconds). */
	durationSec?: number;
	limit?: number;
	/**
	 * `web` = Tavily then Google CSE, then licensed catalogs.
	 * Used for news/documentary stills. Videos stay licensed-first.
	 */
	imagePriority?: "licensed" | "web";
	/** Prefer recent Tavily news images. */
	newsFreshness?: boolean;
}

function hashUrlId(value: string): string {
	let hash = 2166136261;
	for (let index = 0; index < value.length; index += 1) {
		hash ^= value.charCodeAt(index);
		hash = Math.imul(hash, 16777619);
	}
	return (hash >>> 0).toString(16).padStart(8, "0");
}

function pexelsToStock(asset: PexelsAsset): StockAsset {
	const photographer =
		asset.metadata?.photographer || asset.metadata?.user?.name || "Pexels";
	const pageUrl =
		asset.metadata?.photographer_url ||
		`https://www.pexels.com/photo/${asset.pexelsId}/`;
	const preview =
		asset.fileType === "video"
			? asset.metadata?.video_pictures?.[0]?.picture
			: undefined;

	return {
		id: asset.id,
		provider: "pexels",
		fileUrl: asset.fileUrl,
		fileType: asset.fileType,
		width: asset.width,
		height: asset.height,
		duration: asset.duration,
		attribution: `${photographer} (Pexels)`,
		sourcePageUrl: pageUrl,
		externalId: String(asset.pexelsId),
		previewUrl: preview,
	};
}

function wikimediaToStock(asset: WikimediaAsset): StockAsset {
	return {
		id: asset.id,
		provider: "wikimedia",
		fileUrl: asset.fileUrl,
		fileType: asset.fileType,
		width: asset.width,
		height: asset.height,
		duration: asset.duration,
		attribution: asset.attribution,
		sourcePageUrl: asset.sourcePageUrl,
		externalId: asset.id.replace(/^wikimedia_/, ""),
	};
}

function scoreStockAsset(
	asset: StockAsset,
	criteria?: BestMatchCriteria,
): number {
	let score = 0.5;
	if (criteria?.aspectRatio) {
		const ratio = asset.width / Math.max(1, asset.height);
		const isVertical = ratio < 0.8;
		const isHorizontal = ratio > 1.2;
		const isSquare = !isVertical && !isHorizontal;
		if (
			(criteria.aspectRatio === "vertical" && isVertical) ||
			(criteria.aspectRatio === "horizontal" && isHorizontal) ||
			(criteria.aspectRatio === "square" && isSquare)
		) {
			score += 1;
		} else {
			score += 0.3;
		}
	}
	if (criteria?.duration && asset.duration) {
		if (asset.duration >= criteria.duration) score += 1;
		else if (asset.duration >= criteria.duration * 0.8) score += 0.7;
		else score += 0.2;
	}
	if (criteria?.minWidth && asset.width >= criteria.minWidth) score += 0.2;
	if (criteria?.minHeight && asset.height >= criteria.minHeight) score += 0.2;
	return score;
}

async function searchPexelsAsStock(
	query: string,
	type: "image" | "video",
	limit: number,
): Promise<StockAsset[]> {
	try {
		if (type === "video") {
			const res = await searchPexelsVideos(query, { perPage: limit });
			return res.videos
				.map((video) => {
					try {
						const videoFile =
							video.video_files.find(
								(file) => file.quality === "hd" || file.quality === "sd",
							) || video.video_files[0];
						if (!videoFile) return null;
						return pexelsToStock({
							id: `pexels_video_${video.id}`,
							fileUrl: videoFile.link,
							fileType: "video",
							pexelsId: video.id,
							width: video.width,
							height: video.height,
							duration: video.duration,
							metadata: {
								user: video.user,
								video_files: video.video_files,
								video_pictures: video.video_pictures,
							},
						});
					} catch {
						return null;
					}
				})
				.filter((a): a is StockAsset => Boolean(a));
		}

		const res = await searchPexelsImages(query, { perPage: limit });
		return res.photos.map((photo) =>
			pexelsToStock({
				id: `pexels_${photo.id}`,
				fileUrl: photo.src.large2x || photo.src.original,
				fileType: "image",
				pexelsId: photo.id,
				width: photo.width,
				height: photo.height,
				metadata: {
					photographer: photo.photographer,
					photographer_url: photo.photographer_url,
					alt: photo.alt,
					avg_color: photo.avg_color,
				},
			}),
		);
	} catch (error) {
		console.warn("[searchStockAssets] Pexels search failed:", error);
		return [];
	}
}

async function searchWikimediaAsStock(
	query: string,
	type: "image" | "video",
	limit: number,
): Promise<StockAsset[]> {
	try {
		const assets = await searchWikimediaAssets(query, type, { limit });
		return assets.map(wikimediaToStock);
	} catch (error) {
		console.warn("[searchStockAssets] Wikimedia search failed:", error);
		return [];
	}
}

async function searchTavilyAsStock(
	query: string,
	type: "image" | "video",
	limit: number,
	options?: {
		openWeb?: boolean;
		newsFreshness?: boolean;
	},
): Promise<StockAsset[]> {
	if (type !== "image" || !hasTavilyApiKey()) return [];
	try {
		const response = await tavilySearch({
			query,
			topic: options?.newsFreshness ? "news" : "general",
			searchDepth: "basic",
			maxResults: Math.min(8, limit),
			timeRange: options?.newsFreshness ? "week" : undefined,
			includeImages: true,
			includeImageDescriptions: true,
			includeDomains: options?.openWeb
				? undefined
				: [
						"pexels.com",
						"unsplash.com",
						"commons.wikimedia.org",
						"upload.wikimedia.org",
						"flickr.com",
					],
		});
		return response.images.slice(0, limit).map((image) => {
			const id = hashUrlId(image.url);
			return {
				id: `tavily_${id}`,
				provider: "tavily" as const,
				fileUrl: image.url,
				fileType: "image" as const,
				width: 1280,
				height: 720,
				attribution: image.description?.trim()
					? `${image.description.trim()} (web)`
					: "Web image via Tavily",
				sourcePageUrl: image.url,
				externalId: id,
			};
		});
	} catch (error) {
		console.warn("[searchStockAssets] Tavily image search failed:", error);
		return [];
	}
}

async function searchGoogleAsStock(
	query: string,
	type: "image" | "video",
	limit: number,
): Promise<StockAsset[]> {
	if (type !== "image" || !hasGoogleCseConfig()) return [];
	try {
		const hits = await searchGoogleCseImages(query, limit);
		return hits.map((hit) => {
			const id = hashUrlId(hit.url);
			return {
				id: `google_${id}`,
				provider: "google" as const,
				fileUrl: hit.url,
				fileType: "image" as const,
				width: hit.width && hit.width > 0 ? hit.width : 1280,
				height: hit.height && hit.height > 0 ? hit.height : 720,
				attribution: hit.title?.trim()
					? `${hit.title.trim()} (Google)`
					: "Web image via Google",
				sourcePageUrl: hit.contextUrl || hit.url,
				externalId: id,
			};
		});
	} catch (error) {
		console.warn("[searchStockAssets] Google CSE image search failed:", error);
		return [];
	}
}

function dedupeStockAssets(assets: StockAsset[]): StockAsset[] {
	const seen = new Set<string>();
	const merged: StockAsset[] = [];
	for (const asset of assets) {
		const key = `${asset.provider}:${asset.externalId}:${asset.fileUrl}`;
		if (seen.has(key)) continue;
		seen.add(key);
		merged.push(asset);
	}
	return merged;
}

/**
 * Search image/video catalogs and return ranked candidates.
 * `imagePriority=web` (news/documentary stills): Tavily, then Google CSE,
 * then Pexels/Wikimedia. Videos and fictional stills stay licensed-first.
 */
export async function searchStockAssets(
	input: SearchStockAssetsInput,
): Promise<StockAsset[]> {
	const query = input.query.trim();
	if (!query) return [];

	const limit = Math.min(30, Math.max(1, input.limit ?? 12));
	const criteria: BestMatchCriteria = {
		aspectRatio: input.aspectRatio,
		duration: input.durationSec,
		minWidth: 640,
		minHeight: 480,
	};
	const webFirst = input.type === "image" && input.imagePriority === "web";

	let pexels: StockAsset[] = [];
	let wikimedia: StockAsset[] = [];
	let tavily: StockAsset[] = [];
	let google: StockAsset[] = [];

	if (webFirst) {
		[tavily, google, pexels, wikimedia] = await Promise.all([
			searchTavilyAsStock(query, input.type, limit, {
				openWeb: true,
				newsFreshness: Boolean(input.newsFreshness),
			}),
			searchGoogleAsStock(query, input.type, limit),
			searchPexelsAsStock(query, input.type, limit),
			searchWikimediaAsStock(query, input.type, limit),
		]);
	} else {
		[pexels, wikimedia] = await Promise.all([
			searchPexelsAsStock(query, input.type, limit),
			searchWikimediaAsStock(query, input.type, limit),
		]);
		if (input.type === "image" && pexels.length === 0 && wikimedia.length === 0)
			tavily = await searchTavilyAsStock(query, input.type, limit);
	}

	const ordered = webFirst
		? [...tavily, ...google, ...pexels, ...wikimedia]
		: [...pexels, ...wikimedia, ...tavily];

	const merged = dedupeStockAssets(ordered);

	return merged
		.map((asset) => {
			let score = scoreStockAsset(asset, criteria);
			if (
				webFirst &&
				(asset.provider === "tavily" || asset.provider === "google")
			)
				score += 1.5;
			return { asset, score };
		})
		.filter((row) => row.score >= 0.4)
		.sort((a, b) => b.score - a.score)
		.slice(0, limit)
		.map((row) => row.asset);
}

export { inferPexelsAspectRatioPreference };
